import type {
  ApplyDonationSettlementObservationInput,
  ApplyDonationSettlementObservationResponse,
  BanquestWebhook,
  BanquestWebhookListResponse,
  ClaimImportedBanquestTransactionInput,
  CreateBanquestWebhookInput,
  CreateDonationDafRecommendationInput,
  DeleteBanquestWebhookInput,
  DonationDafRecommendationResponse,
  DonationFundingConfig,
  CompleteStripeConnectedAccountOnboardingInput,
  EnsureStripeCustomerInput,
  EnsureStripeCustomerResponse,
  GetBanquestTransactionInput,
  HostedPaymentSessionInput,
  HostedPaymentSessionResponse,
  HostedPortalSessionResponse,
  ImportBanquestTransactionObservationInput,
  PaymentManagementContext,
  PaymentManagementContextInput,
  PaymentControlPlane,
  PaymentControlPlaneReadiness,
  PaymentControlPlaneReadinessInput,
  PaymentControlPlaneUpdateInput,
  PaymentEventEnvelope,
  PaymentMirrorLedgerEntry,
  PaymentMirrorLedgerListResponse,
  PaymentMirrorLedgerQuery,
  PendingDonationSettlementListResponse,
  ProviderTransactionObservation,
  ProviderTransactionObservationListResponse,
  RefreshImportedBanquestTransactionInput,
  SearchBanquestTransactionsInput,
  StartStripeConnectedAccountOnboardingInput,
  StripeConnectedAccountOnboardingSession,
  SyncBanquestWebhookStateInput,
  UpdateBanquestWebhookInput,
  VoidDonationDafRecommendationInput,
  VoidDonationDafRecommendationResponse,
} from '../contracts/index';
import type { DomainRouterJsonInput } from './domain-router-transport';

export const PAYMENTS_QUERY_OPERATION_IDS = [
  'getPaymentControlPlane',
  'getPaymentControlPlaneReadiness',
  'getDonationFundingConfig',
  'listPendingDonationSettlements',
  'getMyPaymentManagementContext',
  'listPaymentMirrorLedgerEntries',
  'listBanquestWebhooks',
  'searchBanquestTransactions',
  'getBanquestTransaction',
] as const;

export const PAYMENTS_MUTATION_OPERATION_IDS = [
  'updatePaymentControlPlane',
  'startStripeConnectedAccountOnboarding',
  'completeStripeConnectedAccountOnboarding',
  'ensureStripeCustomer',
  'createHostedPaymentSession',
  'createHostedPortalSession',
  'createDonationDafRecommendation',
  'voidDonationDafRecommendation',
  'applyDonationSettlementObservation',
  'recordPaymentEventEnvelope',
  'createBanquestWebhook',
  'updateBanquestWebhook',
  'deleteBanquestWebhook',
  'syncBanquestWebhookState',
  'importBanquestTransactionObservation',
  'claimImportedBanquestTransaction',
  'refreshImportedBanquestTransaction',
] as const;

export type PaymentsQueryOperationId = (typeof PAYMENTS_QUERY_OPERATION_IDS)[number];
export type PaymentsMutationOperationId = (typeof PAYMENTS_MUTATION_OPERATION_IDS)[number];

export type PaymentsQueryArgsMap = {
  getPaymentControlPlane: { input?: DomainRouterJsonInput };
  getPaymentControlPlaneReadiness: { input?: PaymentControlPlaneReadinessInput | DomainRouterJsonInput };
  getDonationFundingConfig: { input?: DomainRouterJsonInput };
  listPendingDonationSettlements: { input?: DomainRouterJsonInput };
  getMyPaymentManagementContext: { input?: PaymentManagementContextInput | DomainRouterJsonInput };
  listPaymentMirrorLedgerEntries: { input?: PaymentMirrorLedgerQuery | DomainRouterJsonInput };
  listBanquestWebhooks: { input?: DomainRouterJsonInput };
  searchBanquestTransactions: { input?: SearchBanquestTransactionsInput | DomainRouterJsonInput };
  getBanquestTransaction: { input: GetBanquestTransactionInput | DomainRouterJsonInput };
};

export type PaymentsQueryResultMap = {
  getPaymentControlPlane: PaymentControlPlane;
  getPaymentControlPlaneReadiness: PaymentControlPlaneReadiness;
  getDonationFundingConfig: DonationFundingConfig;
  listPendingDonationSettlements: PendingDonationSettlementListResponse;
  getMyPaymentManagementContext: PaymentManagementContext;
  listPaymentMirrorLedgerEntries: PaymentMirrorLedgerListResponse;
  listBanquestWebhooks: BanquestWebhookListResponse;
  searchBanquestTransactions: ProviderTransactionObservationListResponse;
  getBanquestTransaction: ProviderTransactionObservation;
};

export type PaymentsMutationArgsMap = {
  updatePaymentControlPlane: { input: PaymentControlPlaneUpdateInput | DomainRouterJsonInput };
  startStripeConnectedAccountOnboarding: {
    input: StartStripeConnectedAccountOnboardingInput | DomainRouterJsonInput;
  };
  completeStripeConnectedAccountOnboarding: {
    input: CompleteStripeConnectedAccountOnboardingInput | DomainRouterJsonInput;
  };
  ensureStripeCustomer: { input: EnsureStripeCustomerInput | DomainRouterJsonInput };
  createHostedPaymentSession: { input: HostedPaymentSessionInput | DomainRouterJsonInput };
  createHostedPortalSession: { input: DomainRouterJsonInput };
  createDonationDafRecommendation: {
    input: CreateDonationDafRecommendationInput | DomainRouterJsonInput;
  };
  voidDonationDafRecommendation: {
    input: VoidDonationDafRecommendationInput | DomainRouterJsonInput;
  };
  applyDonationSettlementObservation: {
    input: ApplyDonationSettlementObservationInput | DomainRouterJsonInput;
  };
  recordPaymentEventEnvelope: { input: PaymentEventEnvelope | DomainRouterJsonInput };
  createBanquestWebhook: { input: CreateBanquestWebhookInput | DomainRouterJsonInput };
  updateBanquestWebhook: { input: UpdateBanquestWebhookInput | DomainRouterJsonInput };
  deleteBanquestWebhook: { input: DeleteBanquestWebhookInput | DomainRouterJsonInput };
  syncBanquestWebhookState: { input?: SyncBanquestWebhookStateInput | DomainRouterJsonInput };
  importBanquestTransactionObservation: {
    input: ImportBanquestTransactionObservationInput | DomainRouterJsonInput;
  };
  claimImportedBanquestTransaction: {
    input: ClaimImportedBanquestTransactionInput | DomainRouterJsonInput;
  };
  refreshImportedBanquestTransaction: {
    input: RefreshImportedBanquestTransactionInput | DomainRouterJsonInput;
  };
};

export type PaymentsMutationResultMap = {
  updatePaymentControlPlane: PaymentControlPlane;
  startStripeConnectedAccountOnboarding: StripeConnectedAccountOnboardingSession;
  completeStripeConnectedAccountOnboarding: PaymentControlPlane;
  ensureStripeCustomer: EnsureStripeCustomerResponse;
  createHostedPaymentSession: HostedPaymentSessionResponse;
  createHostedPortalSession: HostedPortalSessionResponse;
  createDonationDafRecommendation: DonationDafRecommendationResponse;
  voidDonationDafRecommendation: VoidDonationDafRecommendationResponse;
  applyDonationSettlementObservation: ApplyDonationSettlementObservationResponse;
  recordPaymentEventEnvelope: PaymentMirrorLedgerEntry;
  createBanquestWebhook: BanquestWebhook;
  updateBanquestWebhook: BanquestWebhook;
  deleteBanquestWebhook: { ok: boolean; webhookId: number };
  syncBanquestWebhookState: PaymentControlPlane;
  importBanquestTransactionObservation: ProviderTransactionObservation;
  claimImportedBanquestTransaction: ProviderTransactionObservation;
  refreshImportedBanquestTransaction: ProviderTransactionObservation;
};
